import { auth, db } from '../firebase';
import { 
  collection, 
  getDocs 
} from 'firebase/firestore';

// Track in-flight summary requests per contentId to ensure only one request at a time per session
const inFlightSummaryRequests = new Map();

// Expose a lightweight checker for callers to avoid triggering duplicate requests
export const isSummaryRequestInFlight = (contentId) => inFlightSummaryRequests.has(contentId);

// Cache endpoint from environment
const SUMMARY_API = process.env.REACT_APP_SUMMARY_API;

// ----------------- Generate summary for a session -----------------
export const generateSummaryForSession = async (contentId, options = {}) => {
  if (!contentId) throw new Error("Missing contentId for summary generation");

  // If a request for this contentId is already in flight, return the same promise
  if (inFlightSummaryRequests.has(contentId)) {
    return inFlightSummaryRequests.get(contentId);
  }

  const promise = (async () => {
    const { promptStyle = "Detailed Summary", maxPoints = 8 } = options;

    try {
      if (!SUMMARY_API) {
        throw new Error('Summary API endpoint not configured. Set REACT_APP_SUMMARY_API in .env');
      }
      const user = auth.currentUser;
      let token = null;
      if (user) {
        token = await user.getIdToken();
      }

      const payload = {
        contentId,
        promptStyle,
        maxPoints
      };

      const resp = await fetch(SUMMARY_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Summary API error: ${resp.status} ${txt}`);
      }

      const data = await resp.json();
      if (!data || data.success === false) {
        throw new Error(`Summary API failed: ${JSON.stringify(data)}`);
      }

      // Use backend-provided values when available
      const title = data.title || (data.result && data.result.title) || "Summary";
      const points = Array.isArray(data.points) ? data.points : ((data.result && data.result.points) || []);

      return { title, points };

    } catch (err) {
      console.error("[summaryService] generateSummaryForSession error:", err);
      throw err;
    } finally {
      // Clear in-flight marker when finished (success or error)
      inFlightSummaryRequests.delete(contentId);
    }
  })();

  inFlightSummaryRequests.set(contentId, promise);
  return promise;
};

// ----------------- Fetch all saved summaries for a session -----------------
export const fetchSavedSummaries = async (contentId) => {
  try {
    const user = auth.currentUser;
    if (!user || !contentId) return [];

    const summaryCollectionRef = collection(db, 'users', user.uid, 'Sessions', contentId, 'Summary');
    const snap = await getDocs(summaryCollectionRef);

    // Return each doc as { id, summary, timestamp } so caller can choose latest
    const summaries = snap.docs.map(d => {
      const data = d.data() || {};
      return {
        id: d.id,
        summary: data.summary ?? null,
        timestamp: data.timestamp ?? null
      };
    });

    return summaries;
  } catch (e) {
    console.error("[summaryService] fetchSavedSummaries error:", e);
    return [];
  }
};
